/**
 * Small presence indicator pills — shows green dots for everyone currently
 * connected to the case (excluding the local user), plus a "typing..." line.
 */
import { Circle } from "@phosphor-icons/react";

const ROLE_COLOR = {
  solicitor: "bg-emerald-500",
  client: "bg-blue-500",
  referrer: "bg-violet-500",
};
const ROLE_LABEL = {
  solicitor: "Solicitor",
  client: "Client",
  referrer: "Referrer",
};

export function PresenceBar({ peers, typingActors }) {
  if ((!peers || peers.length === 0) && (!typingActors || typingActors.length === 0)) return null;
  // Dedupe by name+role
  const seen = new Set();
  const uniquePeers = (peers || []).filter((p) => {
    const k = `${p.actor_role}:${p.actor_name}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const typingLabel = (typingActors || []).length === 0 ? null
    : typingActors.length === 1 ? `${typingActors[0].actor_name} is typing…`
    : `${typingActors.length} people are typing…`;

  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="presence-bar">
      {uniquePeers.map((p) => (
        <span key={`${p.actor_role}:${p.actor_name}`}
          data-testid={`presence-${p.actor_role}-${p.actor_name}`}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
          <span className={`w-1.5 h-1.5 rounded-full ${ROLE_COLOR[p.actor_role] || "bg-slate-400"} animate-pulse`} />
          <span className="font-medium">{p.actor_name}</span>
          <span className="text-slate-400">· {ROLE_LABEL[p.actor_role] || p.actor_role}</span>
        </span>
      ))}
      {typingLabel && (
        <span className="inline-flex items-center gap-1 text-slate-500 italic" data-testid="typing-indicator">
          <Circle weight="fill" className="w-1 h-1 animate-pulse" />
          <Circle weight="fill" className="w-1 h-1 animate-pulse" style={{ animationDelay: "150ms" }} />
          <Circle weight="fill" className="w-1 h-1 animate-pulse" style={{ animationDelay: "300ms" }} />
          <span className="ml-1">{typingLabel}</span>
        </span>
      )}
    </div>
  );
}
